import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useMemo } from 'react';
import { queryPipelineStageFilterCounts } from '@/features/pipelines/data';
import {
  buildStageNameFilters,
  buildStagePipelineFilters,
} from '@/features/pipelines/hooks/stagePageFilters';
import { buildCountedDropdownOptions } from '@/features/shared/components/tableSystem/filterOptionCounts';
import { queryKeys } from '@/shared/hooks';
import {
  buildContainsFilter,
  buildDateRangeFilters,
  buildInFilter,
  mergeFilters,
} from '@/ui/composites/grid/gridFilters';
import type { TableDropdownOption } from '@/features/shared/components/tableSystem/tableToolbar.types';
import type { BuildStatus } from '@flowqa/shared';

type UseStagesPageFilterOptionCountsArgs = {
  effectiveEndDay: string | null;
  effectiveProjectId: string | null;
  effectiveStartDay: string | null;
  hasLatestTimestamp: boolean;
  isAuthenticated: boolean;
  isLatestMode: boolean;
  pipelineOptions: readonly TableDropdownOption[];
  selectedOrganisationId: string | null;
  selectedPipelineIds: string[];
  selectedStageNames: string[];
  selectedStatuses: BuildStatus[];
  selectedTags: string[];
  stageOptions: readonly TableDropdownOption[];
  stageSearchValue: string;
  tagOptions: readonly TableDropdownOption[];
};

type CountedStageFilterOptions = {
  countedPipelineOptions: TableDropdownOption[];
  countedStageOptions: TableDropdownOption[];
  countedTagOptions: TableDropdownOption[];
};

export const useStagesPageFilterOptionCounts = ({
  effectiveEndDay,
  effectiveProjectId,
  effectiveStartDay,
  hasLatestTimestamp,
  isAuthenticated,
  isLatestMode,
  pipelineOptions,
  selectedOrganisationId,
  selectedPipelineIds,
  selectedStageNames,
  selectedStatuses,
  selectedTags,
  stageOptions,
  stageSearchValue,
  tagOptions,
}: UseStagesPageFilterOptionCountsArgs): CountedStageFilterOptions => {
  const stageCountDateFilters = useMemo(
    () =>
      buildDateRangeFilters(
        'startedAt',
        effectiveStartDay ? dayjs(effectiveStartDay).startOf('day').toISOString() : null,
        effectiveEndDay ? dayjs(effectiveEndDay).endOf('day').toISOString() : null,
      ),
    [effectiveEndDay, effectiveStartDay],
  );
  const pipelineCountStageFilters = useMemo(
    () =>
      mergeFilters(
        buildContainsFilter('stageName', stageSearchValue),
        buildInFilter('status', selectedStatuses),
        buildStageNameFilters(selectedStageNames),
        buildInFilter('tags', selectedTags),
        stageCountDateFilters,
      ),
    [selectedStageNames, selectedStatuses, selectedTags, stageCountDateFilters, stageSearchValue],
  );
  const stageNameCountStageFilters = useMemo(
    () =>
      mergeFilters(
        buildContainsFilter('stageName', stageSearchValue),
        buildInFilter('status', selectedStatuses),
        buildStagePipelineFilters(selectedPipelineIds),
        buildInFilter('tags', selectedTags),
        stageCountDateFilters,
      ),
    [selectedPipelineIds, selectedStatuses, selectedTags, stageCountDateFilters, stageSearchValue],
  );
  const tagCountStageFilters = useMemo(
    () =>
      mergeFilters(
        buildContainsFilter('stageName', stageSearchValue),
        buildInFilter('status', selectedStatuses),
        buildStagePipelineFilters(selectedPipelineIds),
        buildStageNameFilters(selectedStageNames),
        stageCountDateFilters,
      ),
    [
      selectedPipelineIds,
      selectedStageNames,
      selectedStatuses,
      stageCountDateFilters,
      stageSearchValue,
    ],
  );
  const countGroups = useMemo(
    () => ({
      pipelines: pipelineCountStageFilters,
      stages: stageNameCountStageFilters,
      tags: tagCountStageFilters,
    }),
    [pipelineCountStageFilters, stageNameCountStageFilters, tagCountStageFilters],
  );
  const filterCountsQuery = useQuery({
    queryKey: queryKeys.dataGrid(
      'stages/page',
      'filter-counts',
      selectedOrganisationId,
      effectiveProjectId,
      JSON.stringify(countGroups),
    ),
    enabled: isAuthenticated && (!isLatestMode || hasLatestTimestamp),
    queryFn: async () =>
      await queryPipelineStageFilterCounts(effectiveProjectId, selectedOrganisationId, {
        filters: countGroups,
      }),
  });

  return useMemo(
    () => ({
      countedPipelineOptions: buildCountedDropdownOptions(
        pipelineOptions,
        filterCountsQuery.data?.pipelines ?? {},
      ),
      countedStageOptions: buildCountedDropdownOptions(
        stageOptions,
        filterCountsQuery.data?.stages ?? {},
      ),
      countedTagOptions: buildCountedDropdownOptions(
        tagOptions,
        filterCountsQuery.data?.tags ?? {},
      ),
    }),
    [
      filterCountsQuery.data?.pipelines,
      filterCountsQuery.data?.stages,
      filterCountsQuery.data?.tags,
      pipelineOptions,
      stageOptions,
      tagOptions,
    ],
  );
};
